import { useMemo, useState, useCallback } from "react";
import Select from "react-select";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useCollection } from "../../hooks/useCollection";
import { useDocument } from "../../hooks/useDocument";
import { useFirestore } from "../../hooks/useFirestore";

// Import styles from Dashboard to reuse table styles
import "../dashboard/Dashboard.css";

export default function RichiesteInfoTecnico() {
  const { user } = useAuthContext();
  const { documents: richieste } = useCollection("richiesteInfo", null, [
    "createdAt",
    "desc",
  ]);
  const { documents: projects } = useCollection("projects");
  const { document: userDoc } = useDocument("users", user.uid);
  const { updateDocument } = useFirestore("richiesteInfo");

  const [selectedCantiere, setSelectedCantiere] = useState({});
  const [isUpdating, setIsUpdating] = useState({});
  const [modalImage, setModalImage] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "data", dir: "desc" });

  const userSectorCode =
    userDoc && userDoc.anagrafica && userDoc.anagrafica.settore
      ? userDoc.anagrafica.settore
      : null;

  const matchesUserSector = useCallback(
    (nomeSettore) => {
      if (!userSectorCode || !nomeSettore) return false;
      if (nomeSettore === userSectorCode) return true;
      if (nomeSettore.includes(userSectorCode)) return true;
      if (userSectorCode.includes(nomeSettore)) return true;
      return false;
    },
    [userSectorCode],
  );

  const projectOptions = useMemo(() => {
    if (!projects) return [];
    return projects
      .filter((p) => !p.archived)
      .map((p) => ({
        value: p.id,
        label: `${p.categoria || ""} - ${p.nome || p.codice || p.id}`,
      }));
  }, [projects]);

  const pendingRequests = useMemo(() => {
    if (!richieste || !userSectorCode) return [];
    return richieste.filter((r) => {
      if (!r.settori || !Array.isArray(r.settori)) return false;
      const settoreEntry = r.settori.find((s) => matchesUserSector(s.nome));
      return settoreEntry && settoreEntry.status === "pending";
    });
  }, [richieste, userSectorCode, matchesUserSector]);

  const sortedRequests = useMemo(() => {
    if (!pendingRequests) return [];
    const sorted = [...pendingRequests];
    const collator = new Intl.Collator("it", {
      sensitivity: "base",
      numeric: true,
    });

    sorted.sort((a, b) => {
      let valA = "";
      let valB = "";

      switch (sortConfig.key) {
        case "data":
          valA = a.data?.toDate
            ? a.data.toDate().getTime()
            : new Date(a.data).getTime();
          valB = b.data?.toDate
            ? b.data.toDate().getTime()
            : new Date(b.data).getTime();
          break;
        case "fornitore":
          valA = a.fornitore || "";
          valB = b.fornitore || "";
          break;
        case "importo":
          valA = Number(a.importo) || 0;
          valB = Number(b.importo) || 0;
          break;
        case "protocollo":
          valA = a.protocollo || "";
          valB = b.protocollo || "";
          break;
        case "scadenza":
          valA = a.scadenza?.toDate
            ? a.scadenza.toDate().getTime()
            : a.scadenza
              ? new Date(a.scadenza).getTime()
              : 0;
          valB = b.scadenza?.toDate
            ? b.scadenza.toDate().getTime()
            : b.scadenza
              ? new Date(b.scadenza).getTime()
              : 0;
          break;
        default:
          valA = "";
          valB = "";
      }

      let cmp;
      if (typeof valA === "number" && typeof valB === "number") {
        cmp = valA - valB;
      } else {
        cmp = collator.compare(valA, valB);
      }
      return sortConfig.dir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [pendingRequests, sortConfig]);

  const handleHeaderSort = (key) => {
    setSortConfig((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
  };

  const handleNotMySector = async (r) => {
    setIsUpdating((prev) => ({ ...prev, [r.id]: true }));
    try {
      const updatedSettori =
        r.settori && Array.isArray(r.settori)
          ? r.settori.map((s) =>
              matchesUserSector(s.nome)
                ? { ...s, status: "closed-not-my-sector" }
                : s,
            )
          : r.settori;
      await updateDocument(r.id, {
        settori: updatedSettori,
        outcome: "not-my-sector",
        handledBy: {
          uid: user.uid,
          displayName: user.displayName || user.email,
        },
      });
    } finally {
      setIsUpdating((prev) => ({ ...prev, [r.id]: false }));
    }
  };

  const handleAssignProject = async (r) => {
    const sel = selectedCantiere[r.id];
    if (!sel) return;

    setIsUpdating((prev) => ({ ...prev, [r.id]: true }));
    try {
      const project =
        projects && Array.isArray(projects)
          ? projects.find((p) => p.id === sel.value)
          : null;
      const settoreConfermante =
        r.settori && Array.isArray(r.settori)
          ? r.settori.find((s) => matchesUserSector(s.nome))
          : null;
      const updatedSettori =
        r.settori && Array.isArray(r.settori)
          ? r.settori.map((s) => {
              if (settoreConfermante && s.id === settoreConfermante.id) {
                return { ...s, status: "assigned" };
              }
              if (s.status === "pending") {
                return { ...s, status: "closed-assigned" };
              }
              return s;
            })
          : r.settori;
      await updateDocument(r.id, {
        status: "closed",
        outcome: "assigned",
        projectId: sel.value,
        projectNome: sel.label,
        projectCodice: project && project.codice ? project.codice : null,
        assignedSectorId: settoreConfermante ? settoreConfermante.id : null,
        assignedSectorNome: settoreConfermante ? settoreConfermante.nome : null,
        settori: updatedSettori,
        handledBy: {
          uid: user.uid,
          displayName: user.displayName || user.email,
        },
      });
      setSelectedCantiere((prev) => {
        const copy = { ...prev };
        delete copy[r.id];
        return copy;
      });
    } finally {
      setIsUpdating((prev) => ({ ...prev, [r.id]: false }));
    }
  };

  return (
    <div
      className="page-container"
      style={{ padding: "24px 16px", backgroundColor: "#f5f7fb" }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ marginBottom: "24px" }}>
          <h1 className="page-title" style={{ marginBottom: "4px" }}>
            Richieste Info Documenti
          </h1>
          <p
            style={{
              margin: 0,
              marginBottom: "8px",
              color: "#555",
              maxWidth: "640px",
            }}
          >
            Qui puoi vedere le richieste aperte dall&apos;amministrazione e
            indicare se una fattura è di tua competenza, associandola a un
            cantiere, oppure segnalarla come non di tua competenza.
          </p>
        </div>

        {!richieste && <p>Caricamento richieste...</p>}
        {pendingRequests.length === 0 && richieste && (
          <p>Non ci sono richieste aperte.</p>
        )}

        {richieste && pendingRequests.length > 0 && (
          <div
            style={{
              marginBottom: "16px",
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: "0.9rem", color: "#666" }}>
              Ordina per:
            </span>
            {[
              { key: "data", label: "Data" },
              { key: "fornitore", label: "Fornitore" },
              { key: "importo", label: "Importo" },
            ].map((item) => (
              <button
                key={item.key}
                type="button"
                className="btn"
                onClick={() => handleHeaderSort(item.key)}
                style={{
                  backgroundColor:
                    sortConfig.key === item.key ? "#e0e0e0" : "#ffffff",
                  color: "#333",
                  border: "1px solid #ccc",
                  padding: "4px 12px",
                  fontSize: "0.85rem",
                  fontWeight: sortConfig.key === item.key ? "bold" : "normal",
                }}
              >
                {item.label}
                {sortConfig.key === item.key && (
                  <span>
                    {sortConfig.dir === "asc" ? " \u2191" : " \u2193"}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "16px",
          }}
        >
          {sortedRequests.map((r) => (
            <div
              key={r.id}
              style={{
                borderRadius: "12px",
                border: "1px solid #e0e0e0",
                padding: "16px",
                backgroundColor: "#ffffff",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                borderLeft: "4px solid #ffc107",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <span style={{ fontSize: "0.85rem", color: "#666" }}>
                  {r.data &&
                    (r.data.toDate
                      ? format(r.data.toDate(), "d MMM yyyy", { locale: it })
                      : format(new Date(r.data), "d MMM yyyy", { locale: it }))}
                </span>
                <span
                  style={{
                    fontSize: "0.75rem",
                    padding: "2px 8px",
                    borderRadius: "999px",
                    backgroundColor: "#ffc107",
                    color: "#fff",
                    textTransform: "uppercase",
                  }}
                >
                  pending
                </span>
              </div>

              <h3
                style={{
                  margin: "0 0 4px",
                  fontSize: "1rem",
                  color: "#333",
                }}
              >
                {r.nomeFornitore}
              </h3>
              <p
                style={{
                  margin: "0 0 4px",
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                Importo: € {r.importo}
              </p>
              {r.note && (
                <p style={{ margin: "0 0 8px", fontSize: "0.9rem" }}>
                  {r.note}
                </p>
              )}
              {r.fotoUrl && (
                <div style={{ marginTop: "8px", marginBottom: "4px" }}>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setModalImage(r.fotoUrl)}
                    style={{
                      backgroundColor: "#ffffff",
                      color: "#1976d2",
                      border: "1px solid #1976d2",
                      width: "100%",
                    }}
                  >
                    Apri screenshot fattura
                  </button>
                </div>
              )}

              <div
                style={{
                  marginTop: "10px",
                  paddingTop: "10px",
                  borderTop: "1px solid #eee",
                }}
              >
                <p style={{ marginBottom: "8px", fontWeight: "bold" }}>
                  Risposta:
                </p>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <button
                    type="button"
                    className="btn"
                    onClick={() => handleNotMySector(r)}
                    disabled={!!isUpdating[r.id]}
                    style={{
                      backgroundColor: "#9e9e9e",
                      width: "100%",
                    }}
                  >
                    {isUpdating[r.id]
                      ? "Aggiornamento..."
                      : "Documento non di mia competenza"}
                  </button>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "4px",
                        fontSize: "0.9rem",
                      }}
                    >
                      Se il documento è di tua competenza, seleziona il
                      cantiere:
                    </label>
                    <Select
                      value={selectedCantiere[r.id] || null}
                      onChange={(opt) =>
                        setSelectedCantiere((prev) => ({
                          ...prev,
                          [r.id]: opt,
                        }))
                      }
                      options={projectOptions}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      placeholder="Seleziona cantiere"
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                    />
                    <button
                      type="button"
                      className="btn"
                      onClick={() => handleAssignProject(r)}
                      disabled={!selectedCantiere[r.id] || !!isUpdating[r.id]}
                      style={{
                        marginTop: "8px",
                        backgroundColor: "#4caf50",
                        width: "100%",
                      }}
                    >
                      {isUpdating[r.id]
                        ? "Aggiornamento..."
                        : "Conferma: è di mia competenza"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {modalImage && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setModalImage(null)}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "10px",
              borderRadius: "8px",
              maxWidth: "90vw",
              maxHeight: "90vh",
              boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={modalImage}
              alt="Fattura"
              style={{ maxWidth: "100%", maxHeight: "80vh", display: "block" }}
            />
            <button
              type="button"
              className="btn"
              onClick={() => setModalImage(null)}
              style={{ marginTop: "8px", width: "100%" }}
            >
              Chiudi
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
